import React from 'react';
import { format, addDays, nextMonday, startOfDay } from 'date-fns';

interface DateRangeSelectorProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const handlePreset = (preset: 'next3days' | 'nextweek') => {
    const today = startOfDay(new Date());

    if (preset === 'next3days') {
      onStartDateChange(today);
      // Find the 3rd business day
      let businessDaysFound = 0;
      let currentDate = today;

      while (businessDaysFound < 3) {
        currentDate = addDays(currentDate, 1);
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          businessDaysFound++;
        }
      }

      onEndDateChange(currentDate);
    } else if (preset === 'nextweek') {
      const monday = nextMonday(today);
      onStartDateChange(monday);
      onEndDateChange(addDays(monday, 4)); // Friday
    }
  };

  const formatDateForInput = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  const handleDateInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (date: Date) => void
  ) => {
    const value = e.target.value; // Format: "yyyy-MM-dd"
    if (value) {
      // Parse date components to avoid timezone issues
      const [year, month, day] = value.split('-').map(Number);
      const newDate = new Date(year, month - 1, day, 0, 0, 0, 0);
      setter(newDate);
    }
  };

  return (
    <div className="date-range-selector">
      <label className="section-label">Date Range</label>

      <div className="date-inputs">
        <div className="date-input-group">
          <label htmlFor="start-date">Start Date</label>
          <input
            id="start-date"
            type="date"
            value={formatDateForInput(startDate)}
            onChange={(e) => handleDateInputChange(e, onStartDateChange)}
            className="date-input"
          />
        </div>

        <div className="date-input-group">
          <label htmlFor="end-date">End Date</label>
          <input
            id="end-date"
            type="date"
            value={formatDateForInput(endDate)}
            onChange={(e) => handleDateInputChange(e, onEndDateChange)}
            className="date-input"
            min={formatDateForInput(startDate)}
          />
        </div>
      </div>

      <div className="preset-buttons">
        <button className="preset-btn" onClick={() => handlePreset('next3days')}>
          Next 3 Business Days
        </button>
        <button className="preset-btn" onClick={() => handlePreset('nextweek')}>
          Next Week (Mon–Fri)
        </button>
      </div>
    </div>
  );
};
